import React, { useEffect } from 'react';
import { Row, Column, useToast } from 'Shared/components';

import styles from './styles.module.css';
import {
  Asset,
  Button,
  WarningIcon,
  PaperPlaneIcon,
  YesIcon,
  Alert,
} from '@clearcover/atlas';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import parse from 'date-fns/parse';

import { formatDateShort } from 'Shared/utils/date';
import addDays from 'date-fns/addDays';
import { getErrorMessage } from 'QuoteApp/utils';

type SendESignaturePacketType = {
  eSignatureSMSConsent: boolean | undefined;
};

export const SendESignaturePacket = ({
  eSignatureSMSConsent,
}: SendESignaturePacketType) => {
  const {
    details,
    refreshDetails,
    api: { loading: appApiLoading },
    detailsLoading,
  } = useAppContext();

  const {
    eSignatureErrors,
    setESignatureErrors,
    sendSignaturePacket,
    getSignaturePacket,
    sendSingnaturePacketInProgress,
    loadingSignaturePacket,
    signaturePacketSendAttempt,
  } = usePurchaseContext();
  const { toast } = useToast();

  const isSignaturePacketTriggered = !!signaturePacketSendAttempt;
  const isSignaturePacketCreated =
    signaturePacketSendAttempt?.status === 'packet_created';

  useEffect(() => {
    if (!isSignaturePacketTriggered) {
      getSignaturePacket();
    }
  }, [details, getSignaturePacket, isSignaturePacketTriggered]);

  const signatureDueDate = formatDateShort(
    addDays(
      parse(details.effectiveDate.value, 'MM/dd/yyyy', new Date()),
      45
    ).toISOString()
  );

  const handdleSendSingnaturePacket = async () => {
    if (eSignatureSMSConsent === undefined) {
      setESignatureErrors({
        ...eSignatureErrors,
        smsConsent:
          'Please select whether the customer would like to receive these documents via text message.',
      });

      return;
    }

    if (isSignaturePacketCreated) {
      return;
    }

    try {
      await sendSignaturePacket(eSignatureSMSConsent);

      toast({
        type: 'success',
        message: 'Electronic documents successfully sent.',
        icon: 'info',
        withSecondaryNav: false,
      });

      if (eSignatureErrors?.sentSingnaturePacket) {
        const updatedErrors = { ...eSignatureErrors };
        delete updatedErrors.sentSingnaturePacket;
        setESignatureErrors(updatedErrors);
      }

      await refreshDetails();
    } catch (error) {
      if (error.response?.status === 409) {
        toast({
          type: 'error',
          message: 'Signature packet already sent.',
          icon: 'error',
          withSecondaryNav: false,
        });
        getSignaturePacket();
      } else if (error.response?.status <= 500) {
        toast({
          type: 'error',
          message: getErrorMessage(error),
          withSecondaryNav: false,
          icon: 'error',
        });

        throw error;
      }
    }
  };

  return (
    <>
      <div className={styles.sentESignarutePacketContainer}>
        <Row className={styles.sentESignarutePacketRowWrapper}>
          <Column className={styles.iconColumn}>
            <Asset
              icon={WarningIcon}
              height={16}
              width={16}
              description="warning"
            />
          </Column>
          <Column>
            <p className={styles.topParagraph}>
              Urgent: Document signatures required to prevent coverage
              interruption by {signatureDueDate}.
            </p>
            <p>
              Documents will be sent to your email (
              {details.contactInfo[details.principalInsuredId].email.value})
              {eSignatureSMSConsent
                ? ` and via
            text (${
              details.contactInfo[details.principalInsuredId].phoneNumber.value
            })`
                : ''}
              .
            </p>
            <Row className={styles.buttonRow}>
              <Column className={styles.sendButtonColumn}>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handdleSendSingnaturePacket}
                  disabled={
                    !details.commands.sendSignaturePacket?.runnable ||
                    isSignaturePacketCreated ||
                    sendSingnaturePacketInProgress ||
                    loadingSignaturePacket ||
                    appApiLoading ||
                    detailsLoading
                  }
                  isSubmitting={sendSingnaturePacketInProgress}
                  data-testid="send-signature-packet-button"
                >
                  <Asset
                    icon={PaperPlaneIcon}
                    height={16}
                    width={16}
                    description="send"
                    className={styles.icon}
                  />
                  Send E-Signature Packet
                </Button>
              </Column>
            </Row>
          </Column>
        </Row>
      </div>

      {isSignaturePacketTriggered && (
        <Row>
          <Column>
            <div className={styles.signaturePacketTriggeredMessage}>
              <Asset icon={YesIcon} height={16} width={16} description="send" />
              Documents have been sent.
            </div>
          </Column>
        </Row>
      )}

      {eSignatureErrors?.sentSingnaturePacket && (
        <div data-testid="esignature-send-error-notification">
          <Alert
            message={eSignatureErrors.sentSingnaturePacket}
            className={styles.eSignatureError}
          />
        </div>
      )}
    </>
  );
};
